/**
 * The send-route tag on a listing — the ONE vocabulary for how an application
 * goes out, drawn identically on a match card and on an application (E18/E48/E49).
 *
 * Every posting is one of three routes, decided by applyType() — a property of
 * where the posting's form lives — so a match and the application it becomes can
 * never disagree about how it is sent. The names match the Guide exactly:
 *
 *   Autopilot — a fillable ATS with no human check: JobCork fills AND submits it
 *               from the cloud, your computer off. (None today — every ATS we fill
 *               currently carries a check — so this lights up the day a check-free
 *               source is added.)
 *   Copilot   — a fillable ATS with a bot check: JobCork fills every field and it
 *               goes out from your own Chrome, which clears the "I'm not a robot"
 *               check the cloud cannot. JobCork never defeats the check itself.
 *   Manual    — Workday, iCIMS, an employer's own site, or anywhere repeated
 *               auto-submits would risk your standing: JobCork fills it and you
 *               press the final button. You apply on the link and mark it here.
 *
 * `mode` is Auto's on/off for this track. The visible route is the same either
 * way — the Auto-apply banner is what carries on/off — so `mode` only words the
 * hover; a match passes it, an application does not.
 */

import { applyType } from "./fillHint";

/** The three send routes, named for the seeker. The server decides per posting from the ledger. */
export type SendRoute = "autopilot" | "copilot" | "manual";

/** Fallback when the server hasn't tagged the posting: classify by ATS host alone (every fillable ATS reads as Copilot). */
function routeFromUrl(applyUrl: string | null | undefined): SendRoute {
  const t = applyType(applyUrl);
  return t === "auto" ? "autopilot" : t === "assisted" ? "copilot" : "manual";
}

function CloudIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.6-1.5A4 4 0 0 0 6.5 19z" /><path d="M9 14.5l2 2 4-4" /></svg>;
}
function BrowserIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="15" rx="2" /><path d="M3 8h18" /></svg>;
}
function HandIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 11.5V4.5a1.5 1.5 0 0 1 3 0V11" /><path d="M12 11V3.5a1.5 1.5 0 0 1 3 0V11" /><path d="M15 11V5.5a1.5 1.5 0 0 1 3 0V13a6 6 0 0 1-6 6h-1.5a5 5 0 0 1-4-2l-2.5-3.3a1.5 1.5 0 0 1 2.3-1.9L9 14" /></svg>;
}

export function SendTag({ applyUrl, route, mode }: { applyUrl?: string | null; route?: SendRoute; mode?: "auto" | "review" }) {
  const r = route ?? routeFromUrl(applyUrl);
  if (r === "autopilot") {
    return (
      <span className="route auto" title="Autopilot — the cloud can send at this employer, computer off. JobCork fills and submits it for you.">
        <CloudIcon /> Autopilot <span className="gloss">· sends itself, computer off</span>
      </span>
    );
  }
  if (r === "copilot") {
    return (
      <span
        className="route browser"
        title={mode === "auto"
          ? "Copilot — Auto fills every field and sends it from your own Chrome, which clears the site's “I'm not a robot” check the cloud cannot. Leave Chrome open with the extension."
          : "Copilot — JobCork fills every field and it goes out from your own Chrome, which clears the site's “I'm not a robot” check. Turn Auto on and it sends these for you; JobCork never solves the check itself."}
      >
        <BrowserIcon /> Copilot <span className="gloss">· sends from your open Chrome</span>
      </span>
    );
  }
  return (
    <span className="route manual" title="Manual — a site JobCork can't submit for you (Workday, iCIMS, an employer's own site, or one where repeated auto-submits would risk your standing). JobCork fills what it can; you press the final button, then mark it applied here so it stays tracked.">
      <HandIcon /> Manual <span className="gloss">· we fill it, you press send</span>
    </span>
  );
}
